import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import ShareIcon from "@mui/icons-material/Share";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useAuth } from "../../firebase/authContext";
import {
  saveNote,
  deleteNote,
  saveNoteDraft,
  getNoteDraft,
  clearNoteDraft,
} from "../../firebase/notesService";
import ConfirmDialog from "../ConfirmDialog";
import { cleanVerseQuoteText } from "../../bible/verseQuote";

const PRESET_TAGS = [
  "灵修默想",
  "释经学习",
  "感恩赞美",
  "祷告呼求",
  "生活应用",
  "信心见证",
  "疑难查考",
];

const NoteEditorDialog = ({
  open,
  onClose,
  initialNote = null,
  chapterId,
  bookName,
  chapterNumber,
  verseRange,
  verseText,
  onSaved,
}) => {
  const { currentUser } = useAuth();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState([]);
  const [isPublic, setIsPublic] = useState(false);
  const [newTagInput, setNewTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const draftKey = initialNote?.id || `${chapterId}_${verseRange || "all"}`;

  useEffect(() => {
    if (open) {
      setErrorMsg("");
      if (initialNote) {
        setTitle(initialNote.title || "");
        setContent(initialNote.content || "");
        setTags(initialNote.tags || []);
        setIsPublic(
          Boolean(initialNote.isPublic || initialNote.reviewStatus === "pending")
        );
      } else {
        // Check if there is an unsaved draft
        const draft = getNoteDraft(draftKey);
        if (draft) {
          setTitle(draft.title || "");
          setContent(draft.content || "");
          setTags(draft.tags || []);
          setIsPublic(Boolean(draft.isPublic));
        } else {
          const defaultTitle = `${bookName || ""} ${chapterNumber || 1}${
            verseRange ? `:${verseRange}` : ""
          } 读经心得`;
          setTitle(defaultTitle);
          setContent("");
          setTags(["灵修默想"]);
          setIsPublic(false);
        }
      }
    }
  }, [open, initialNote, chapterId, bookName, chapterNumber, verseRange, draftKey]);

  // Autosave draft when typing (if not already an existing note)
  useEffect(() => {
    if (open && !initialNote && content) {
      saveNoteDraft(draftKey, { title, content, tags, isPublic });
    }
  }, [open, initialNote, draftKey, title, content, tags, isPublic]);

  const handleTogglePresetTag = (tag) => {
    if (tags.includes(tag)) {
      setTags(tags.filter((t) => t !== tag));
    } else {
      if (tags.length < 10) {
        setTags([...tags, tag]);
      }
    }
  };

  const handleAddCustomTag = (e) => {
    if (e.key === "Enter" && newTagInput.trim()) {
      e.preventDefault();
      const trimmed = newTagInput.trim();
      if (!tags.includes(trimmed) && tags.length < 10) {
        setTags([...tags, trimmed]);
      }
      setNewTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleSave = async () => {
    setErrorMsg("");
    if (!content.trim()) {
      setErrorMsg("笔记内容不能为空");
      return;
    }

    setSaving(true);
    try {
      const notePayload = {
        id: initialNote?.id,
        chapterId: chapterId || initialNote?.chapterId,
        bookName: bookName || initialNote?.bookName,
        chapterNumber: chapterNumber || initialNote?.chapterNumber,
        verseRange: verseRange || initialNote?.verseRange || "",
        verseText: cleanVerseQuoteText(verseText || initialNote?.verseText || ""),
        title: title.trim() || `${bookName || ""} ${chapterNumber || 1} 笔记`,
        content: content.trim(),
        tags,
        isPublic,
      };

      const result = await saveNote(notePayload, currentUser);
      clearNoteDraft(draftKey);
      if (onSaved) onSaved(result);
      onClose();
    } catch (err) {
      setErrorMsg(err.message || "保存笔记失败，请重试");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!initialNote?.id) return;
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!initialNote?.id) return;
    setDeleting(true);
    try {
      await deleteNote(initialNote.id, currentUser);
      clearNoteDraft(draftKey);
      setConfirmDeleteOpen(false);
      if (onSaved) onSaved(null);
      onClose();
    } catch (err) {
      setErrorMsg(err.message || "删除笔记失败");
    } finally {
      setDeleting(false);
    }
  };

  const currentBook = bookName || initialNote?.bookName || "";
  const currentChapter = chapterNumber || initialNote?.chapterNumber || 1;
  const currentRange = verseRange || initialNote?.verseRange || "";
  const currentVerseText = cleanVerseQuoteText(
    verseText || initialNote?.verseText || ""
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: { xs: 1, sm: 2 },
          bgcolor: (theme) => theme.palette.background.paper,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, pt: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            {initialNote ? "编辑圣经笔记" : "记录灵修笔记"}
          </Typography>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`${currentBook} 第${currentChapter}章${currentRange ? `:${currentRange}` : ""}`}
            sx={{ fontWeight: 700 }}
          />
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="关闭">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMsg("")}>
            {errorMsg}
          </Alert>
        )}

        {/* Quoted Scripture Preview */}
        {currentVerseText && (
          <Paper
            elevation={0}
            sx={{
              p: 1.75,
              mb: 2.5,
              borderRadius: 2.5,
              border: "1px dashed",
              borderColor: "primary.main",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(25, 118, 210, 0.08)"
                  : "rgba(25, 118, 210, 0.04)",
              position: "relative",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, color: "primary.main" }}>
              <FormatQuoteIcon fontSize="small" />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                所选经文摘录
              </Typography>
            </Box>
            <Typography
              data-testid="note-verse-quote"
              variant="body2"
              sx={{
                fontStyle: "italic",
                color: "text.primary",
                lineHeight: 1.6,
                maxHeight: 120,
                overflowY: "auto",
              }}
            >
              {currentVerseText}
            </Typography>
          </Paper>
        )}

        {/* Title Field */}
        <TextField
          fullWidth
          label="笔记标题"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="例如：对神无条件信实的默想"
          size="small"
          sx={{ mb: 2 }}
        />

        {/* Content Field */}
        <TextField
          fullWidth
          required
          multiline
          minRows={5}
          maxRows={12}
          label="灵修心得与感悟 (支持多行排版)"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="写下圣灵在您心中的感动、生活中的应用与祷告..."
          sx={{ mb: 2.5 }}
        />

        {/* Tags Selection */}
        <Box sx={{ mb: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
            <LocalOfferOutlinedIcon fontSize="small" color="action" />
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
              分类标签 (最多10个)
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
            {PRESET_TAGS.map((tag) => {
              const selected = tags.includes(tag);
              return (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant={selected ? "filled" : "outlined"}
                  color={selected ? "primary" : "default"}
                  onClick={() => handleTogglePresetTag(tag)}
                  sx={{ fontSize: "0.75rem", cursor: "pointer" }}
                />
              );
            })}
          </Box>

          {/* Active Custom Tags */}
          {tags.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
              {tags
                .filter((t) => !PRESET_TAGS.includes(t))
                .map((t) => (
                  <Chip
                    key={t}
                    label={`#${t}`}
                    size="small"
                    color="secondary"
                    onDelete={() => handleRemoveTag(t)}
                    sx={{ fontSize: "0.75rem" }}
                  />
                ))}
            </Box>
          )}

          <TextField
            size="small"
            placeholder="输入自定义标签后按回车添加..."
            value={newTagInput}
            onChange={(e) => setNewTagInput(e.target.value)}
            onKeyDown={handleAddCustomTag}
            sx={{ maxWidth: 260 }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Public Sharing Toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            borderRadius: 2,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.02)",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ pr: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.75 }}>
              <ShareIcon fontSize="small" color={isPublic ? "primary" : "action"} />
              申请公开分享到社区灵修墙与节评
            </Typography>
            <Typography variant="caption" color="text.secondary">
              开启后将提交管理员审核，审核通过后方对其他弟兄姊妹可见（仅展示昵称，不暴露邮箱）
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                color="primary"
              />
            }
            label=""
            sx={{ m: 0 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        {initialNote ? (
          <Button
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={handleDelete}
            disabled={deleting || saving}
            size="small"
            sx={{ textTransform: "none" }}
          >
            {deleting ? "删除中..." : "删除笔记"}
          </Button>
        ) : (
          <Box />
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={saving || deleting}
            size="small"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            取消
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || deleting}
            size="small"
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
          >
            {saving ? "保存中..." : "保存笔记"}
          </Button>
        </Box>
      </DialogActions>
      <ConfirmDialog
        open={confirmDeleteOpen}
        title="删除笔记"
        message="确定要删除这条笔记吗？此操作不可撤销。"
        confirmLabel="删除"
        cancelLabel="取消"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleting) setConfirmDeleteOpen(false);
        }}
      />
    </Dialog>
  );
};

export default NoteEditorDialog;
